import * as React from 'react'
import { cn } from '../lib/utils'

/**
 * SegmentedControl — канон взаимоисключающего выбора из 2–4 фиксированных вариантов
 * (iOS-стиль: дорожка + таблетка). Для фиксируемой настройки с малым числом опций;
 * если вариантов много — <RadioCardGroup>, если это фильтр списка — <Chip>.
 * Дорожка = bg-muted (ниша), выбранный сегмент = таблетка bg-background + тень.
 * Всё скруглено в капсулу, а таблетка переезжает «резинкой»: ведущий край уходит
 * быстро с лёгким перелётом, ведомый подтягивается следом.
 * Нейтральный — бренд не тратится.
 */
export interface SegmentOption<T extends string = string> {
  id: T
  label: string
  icon?: React.ComponentType<{ className?: string }>
  /** Счётчик-кружок справа от подписи (показывается при значении > 0) */
  count?: number
  /** Точка-индикатор «требует внимания» (незаполненное) — бренд */
  dot?: boolean
  /** testid точки-индикатора — нужен потребителям, которым важно найти именно
   *  её (а не сам таб) в тесте; без `dot: true` не рендерится. */
  dotTestId?: string
}

interface SegmentedControlProps<T extends string = string> {
  /** NoInfer: T выводится только из value — иначе id-литералы опций расширяются до string */
  options: SegmentOption<NoInfer<T>>[]
  value: T
  onChange: (id: NoInfer<T>) => void
  /** растянуть сегменты на всю ширину контейнера */
  fullWidth?: boolean
  size?: 'sm' | 'default'
  disabled?: boolean
  className?: string
}

// Резинка: ведущий край — быстро и с перелётом, ведомый — позже и плавно.
const FAST = '240ms cubic-bezier(0.3, 1.25, 0.4, 1)'
const SLOW = '380ms cubic-bezier(0.3, 1, 0.4, 1) 70ms'

type Bar = { left: number; right: number }

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  fullWidth = false,
  size = 'default',
  disabled = false,
  className,
}: SegmentedControlProps<T>) {
  const trackRef = React.useRef<HTMLDivElement>(null)
  const buttonRefs = React.useRef<Record<string, HTMLButtonElement | null>>({})
  const previousIndex = React.useRef(-1)
  const [bar, setBar] = React.useState<Bar | null>(null)
  const [movingRight, setMovingRight] = React.useState(true)
  const activeIndex = options.findIndex((opt) => opt.id === value)

  const measure = React.useCallback(() => {
    const track = trackRef.current
    const button = buttonRefs.current[value]
    if (!track || !button) {
      setBar(null)
      return
    }
    setBar({
      left: button.offsetLeft,
      right: track.clientWidth - (button.offsetLeft + button.offsetWidth),
    })
  }, [value])

  React.useLayoutEffect(() => {
    if (previousIndex.current !== -1) setMovingRight(activeIndex >= previousIndex.current)
    previousIndex.current = activeIndex
    measure()
  }, [activeIndex, measure, options.length])

  React.useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const observer = new ResizeObserver(measure)
    observer.observe(track)
    Object.values(buttonRefs.current).forEach((button) => button && observer.observe(button))
    return () => observer.disconnect()
  }, [measure, options.length])

  return (
    <div
      ref={trackRef}
      role="tablist"
      aria-disabled={disabled}
      className={cn(
        'relative bg-muted rounded-full p-1',
        fullWidth ? 'flex' : 'inline-flex',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      {bar && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute top-1 bottom-1 rounded-full bg-foreground/15 shadow-sm motion-reduce:!transition-none"
          style={{
            left: bar.left,
            right: bar.right,
            transition: movingRight ? `right ${FAST}, left ${SLOW}` : `left ${FAST}, right ${SLOW}`,
          }}
        />
      )}
      {options.map((opt) => {
        const Icon = opt.icon
        const selected = value === opt.id
        return (
          <button
            key={opt.id}
            ref={(el) => {
              buttonRefs.current[opt.id] = el
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={disabled}
            onClick={() => onChange(opt.id)}
            className={cn(
              'relative z-10 flex items-center justify-center gap-1.5 rounded-full font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-sm',
              fullWidth && 'flex-1',
              selected ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />}
            {opt.label}
            {opt.dot && (
              <span
                data-testid={opt.dotTestId}
                className="w-1.5 h-1.5 rounded-full bg-brand shrink-0"
              />
            )}
            {!!opt.count && opt.count > 0 && (
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-full font-semibold tabular-nums',
                  size === 'sm'
                    ? 'min-w-[16px] h-4 px-1 text-[10px]'
                    : 'min-w-[18px] h-[18px] px-1 text-[11px]',
                  selected ? 'bg-brand text-brand-foreground' : 'bg-foreground/15 text-foreground',
                )}
              >
                {opt.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
